import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import type { WorkbookInstance } from '@fortune-sheet/react';
import { isFallbackTarget, isNativeEditableElement } from './shortcutKeymap';
import { CELL_INPUT_ID, FORMULA_BAR_ID, TOOLBAR_SELECTOR } from './selectors';
import { getInternals, moveToEnd } from './workbookInternals';
import { activeCellRowCol, startEditWithText } from './sheetActions';

const BARE_MODIFIER_KEYS = new Set(['Shift', 'Control', 'Alt', 'Meta', 'CapsLock']);

const isPointerFine = (): boolean => window.matchMedia('(pointer: fine)').matches;

const getCellInput = (): HTMLElement | null => document.getElementById(CELL_INPUT_ID);

/** True when `el` is currently a legitimate typing target — restore must
 *  never steal focus away from these. */
const isCurrentlyEditableTarget = (el: Element | null): boolean => {
    if (!el) return false;
    if (el.id === FORMULA_BAR_ID) return true;
    return isNativeEditableElement(el);
};

/** A click inside the library's own toolbar, or inside the drawer chrome
 *  (the BottomDrawer panel) but outside the grid wrapper — "the drawer
 *  header", detected structurally since BottomDrawer exposes no
 *  stable header selector. */
const isToolbarOrHeaderClick = (target: EventTarget | null, wrapperEl: HTMLElement | null): boolean => {
    if (!(target instanceof Element)) return false;
    if (target.closest(TOOLBAR_SELECTOR)) return true;
    const panel = target.closest('[role="dialog"]');
    return Boolean(panel && !(wrapperEl && wrapperEl.contains(target)));
};

const restoreFocus = (): void => {
    if (!isPointerFine()) return;
    const active = document.activeElement;
    if (isCurrentlyEditableTarget(active)) return;
    getCellInput()?.focus({ preventScroll: true });
};

export interface UseSheetFocusArgs {
    /** The grid wrapper div — click restore only fires for `.fortune-toolbar`
     *  clicks inside it, or drawer-chrome clicks outside it. */
    wrapperRef: RefObject<HTMLElement>;
    /** Becomes equal to `workbookKey` once the first paint has landed
     *  (`SheetEditorDrawer.tsx`'s own paint-overlay gate) — the trigger for
     *  restore case (i). */
    paintedKey: string | null;
    workbookKey: string;
    /** True while any app-owned sheet dialog (Rename, Go To,
     *  Format Cells, Paste Special) is open — restore case (ii) fires on the
     *  true→false transition. */
    dialogOpen: boolean;
    /** Grid mounted — gates the toolbar/header click listener. */
    workbookMounted: boolean;
    /** The mounted `<Workbook>`'s own ref — needed to start an edit with the
     *  first printable character. */
    workbookRef: RefObject<WorkbookInstance>;
    /** False during the history preview, which may never start an edit — the
     *  printable-character fallback below is a no-op there. */
    allowEdit: boolean;
}

export interface UseSheetFocusReturn {
    /** Lane B's focus-lost fallback. Call from the document keydown
     *  listener; it decides internally whether it applies. */
    refocusAndRedispatch: (e: KeyboardEvent) => void;
    /** Imperative "focus the cell input now" (pointer:fine-gated, never steals
     *  focus from a legitimate typing target) — the Name Box's Esc-revert
     *  reuses this rather than duplicating the restore logic. */
    focusCellInput: () => void;
}

/**
 * Restores focus to the hidden cell input in the three identified cases
 * (first paint, dialog close, toolbar/header click), and exposes the Lane B
 * focus-lost fallback. Every path is gated on `(pointer: fine)` — on a touch
 * device (e.g. the mobile Ask AICA sheet), programmatic focus of the
 * contenteditable would pop the soft keyboard.
 */
export const useSheetFocus = ({
    wrapperRef,
    paintedKey,
    workbookKey,
    dialogOpen,
    workbookMounted,
    workbookRef,
    allowEdit,
}: UseSheetFocusArgs): UseSheetFocusReturn => {
    // (i) first paint of each workbookKey. React skips this effect when
    // neither dep changed, so a same-key re-render never re-fires it.
    useEffect(() => {
        if (paintedKey !== null && paintedKey === workbookKey) restoreFocus();
    }, [paintedKey, workbookKey]);

    // (ii) closing any sheet dialog.
    const wasDialogOpenRef = useRef(dialogOpen);
    useEffect(() => {
        if (wasDialogOpenRef.current && !dialogOpen) restoreFocus();
        wasDialogOpenRef.current = dialogOpen;
    }, [dialogOpen]);

    // (iii) a toolbar/header click that leaves focus on body or a non-editable element.
    useEffect(() => {
        if (!workbookMounted) return undefined;
        const onClick = (e: MouseEvent) => {
            if (!isToolbarOrHeaderClick(e.target, wrapperRef.current)) return;
            restoreFocus();
        };
        document.addEventListener('click', onClick);
        return () => document.removeEventListener('click', onClick);
    }, [wrapperRef, workbookMounted]);

    const refocusAndRedispatch = (e: KeyboardEvent): void => {
        if (!isFallbackTarget(e.target)) return;
        if (dialogOpen) return;
        if (BARE_MODIFIER_KEYS.has(e.key)) return;
        if (!isPointerFine()) return;
        const cellInput = getCellInput();
        if (!cellInput) return;
        cellInput.focus({ preventScroll: true });
        const isPrintable = e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey;
        if (!isPrintable) {
            const clone = new KeyboardEvent(e.type, {
                key: e.key,
                code: e.code,
                ctrlKey: e.ctrlKey,
                metaKey: e.metaKey,
                shiftKey: e.shiftKey,
                altKey: e.altKey,
                bubbles: true,
                cancelable: true,
            });
            cellInput.dispatchEvent(clone);
        } else if (allowEdit) {
            // A synthetic KeyboardEvent never triggers the browser's own
            // text insertion (only a real, trusted keypress does) —
            // re-dispatching, as the non-printable branch above does, would
            // silently swallow the character. Start the edit with it
            // directly instead, mirroring
            // Excel's own "type to replace" behaviour — the read-only history
            // preview never starts an edit, same as every other mutating path.
            const internals = getInternals(workbookRef.current);
            if (internals) {
                const [r, c] = activeCellRowCol(workbookRef.current, internals);
                startEditWithText(internals, r, c, e.key);
                moveToEnd?.(cellInput);
            }
        }
        e.preventDefault();
        e.stopPropagation();
    };

    return { refocusAndRedispatch, focusCellInput: restoreFocus };
};
